import type { JSONContent } from '@tiptap/core';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkMdx from 'remark-mdx';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import matter from 'gray-matter';
import { remarkCustomAttributes } from './remark-custom-attributes';

// Constants for escaping {{PLACEHOLDER}} patterns before MDX parsing
// MDX interprets {{ as JSX expressions, so we escape them first
// Using %%...%% which won't be interpreted as markdown formatting
const PLACEHOLDER_ESCAPE_PREFIX = '%%TPLPH_';
const PLACEHOLDER_ESCAPE_SUFFIX = '_PHEND%%';

// Custom plugin to handle highlight syntax (==text==)
function remarkHighlight() {
  return (tree: any) => {
    visit(tree, 'text', (node, index, parent) => {
      if (node.value && node.value.includes('==')) {
        const highlightRegex = /==(.*?)==/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = highlightRegex.exec(node.value)) !== null) {
          // Add text before highlight
          if (match.index > lastIndex) {
            parts.push({
              type: 'text',
              value: node.value.slice(lastIndex, match.index)
            });
          }
          
          // Add highlight
          parts.push({
            type: 'highlight',
            children: [{ type: 'text', value: match[1] }]
          });
          
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (lastIndex < node.value.length) {
          parts.push({
            type: 'text',
            value: node.value.slice(lastIndex)
          });
        }
        
        // Replace node with parts if we found highlights
        if (parts.length > 0 && parent && parent.children && index !== undefined) {
          parent.children.splice(index, 1, ...parts);
          return index + parts.length - 1; // Return the index to continue from
        }
      }
    });
  };
}

/**
 * Parse attribute value from string to appropriate type
 */
function parseAttributeValue(value: any, key: string, attrs: Record<string, any>): any {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return true; // For boolean attributes like showIcon
  }

  // Handle MDX expression objects
  if (typeof value === 'object' && value.type === 'mdxJsxAttributeValueExpression') {
    const exprValue = value.value;

    // Handle boolean values
    if (exprValue === 'true') return true;
    if (exprValue === 'false') return false;

    // Handle numbers - only convert if it's clearly meant to be a number
    if (!isNaN(Number(exprValue)) && exprValue !== '' && !exprValue.includes(' ')) {
      return Number(exprValue);
    }

    // Return as string
    return exprValue;
  }

  // Handle string values
  if (typeof value === 'string') {
    if (value === 'undefined' || value === 'null') {
      return undefined;
    }

    // Handle boolean values
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Handle numeric values - convert back to numbers if they look like numbers
    if (!isNaN(Number(value)) && value !== '' && !value.includes(' ')) {
      return Number(value);
    }

    // Check if this attribute is base64 encoded
    const isBase64Encoded = attrs[`data-${key}-encoded`] === 'base64';
    if (isBase64Encoded) {
      try {
        // Remove quotes if present
        const cleanValue = value.replace(/^"|"$/g, '');
        // Decode base64 to string
        const decoded = atob(cleanValue);
        // Parse the decoded JSON string
        return JSON.parse(decoded);
      } catch (error) {
        console.warn(`Failed to decode base64 value for ${key}:`, error);
        return value;
      }
    }

    // Try to parse JSON for complex objects
    if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
      try {
        return JSON.parse(value.replace(/&quot;/g, '"'));
      } catch {
        // If JSON parsing fails, return as string
        return value.replace(/&quot;/g, '"');
      }
    }

    // Unescape quotes and return as string
    return value.replace(/&quot;/g, '"');
  }

  // Fallback
  return value;
}

/**
 * Extract attributes from MDX JSX element
 */
function extractAttributes(mdxNode: any): Record<string, any> {
  const attrs: Record<string, any> = {};

  if (mdxNode.attributes) {
    // First pass: collect all attributes including data-*-encoded flags
    mdxNode.attributes.forEach((attr: any) => {
      const name = attr.name;
      const value = attr.value;
      if (name) {
        attrs[name] = value;
      }
    });

    // Second pass: parse values with access to encoding flags
    mdxNode.attributes.forEach((attr: any) => {
      const name = attr.name;
      // Skip data-*-encoded attributes as they're just flags
      if (!name.endsWith('-encoded')) {
        const value = parseAttributeValue(attr.value, name, attrs);
        if (value !== undefined) {
          attrs[name] = value;
        }
      }
    });

    // Clean up: remove the encoding flag attributes
    Object.keys(attrs).forEach(key => {
      if (key.endsWith('-encoded')) {
        delete attrs[key];
      }
    });
  }

  return attrs;
}

/**
 * Convert MDAST node to TipTap JSONContent
 * May return an array when a text node contains template placeholders
 */
function mdastToTiptap(node: any): JSONContent | JSONContent[] {
  if (!node) return { type: 'text', text: '' };

  // Handle text nodes - also convert escaped template placeholders to templatePlaceholder nodes
  if (node.type === 'text') {
    const text = node.value || '';
    const escapedPlaceholderRegex = new RegExp(`${PLACEHOLDER_ESCAPE_PREFIX}([A-Z_]+)${PLACEHOLDER_ESCAPE_SUFFIX}`, 'g');

    // Check if text contains escaped placeholders
    if (escapedPlaceholderRegex.test(text)) {
      // Reset regex state
      escapedPlaceholderRegex.lastIndex = 0;

      const parts: JSONContent[] = [];
      let lastIndex = 0;
      let match;

      while ((match = escapedPlaceholderRegex.exec(text)) !== null) {
        // Add text before placeholder
        if (match.index > lastIndex) {
          parts.push({ type: 'text', text: text.slice(lastIndex, match.index) });
        }
        // Add placeholder node
        parts.push({ type: 'templatePlaceholder', attrs: { name: match[1] } });
        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push({ type: 'text', text: text.slice(lastIndex) });
      }

      return parts.length === 1 ? parts[0] : parts;
    }

    return { type: 'text', text };
  }

  // Handle inline code
  if (node.type === 'inlineCode') {
    return {
      type: 'text',
      text: node.value,
      marks: [{ type: 'code' }]
    };
  }

  // Handle emphasis (italic)
  if (node.type === 'emphasis') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { type: 'italic' });
  }

  // Handle strong (bold)
  if (node.type === 'strong') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { type: 'bold' });
  }

  // Handle delete (strikethrough)
  if (node.type === 'delete') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { type: 'strike' });
  }

  // Handle highlight marks (==text==)
  if (node.type === 'highlight') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { 
      type: 'highlight',
      attrs: { color: 'rgba(168, 85, 247, 0.2)' }
    });
  }

  // Handle links
  if (node.type === 'link') {
    const children = convertChildren(node.children);
    const attrs: any = { href: node.url || '' };
    // The title attribute in markdown links contains the target
    if (node.title) {
      attrs.target = node.title;
    }
    return addMarkToChildren(children, {
      type: 'link',
      attrs
    });
  }

  // Handle paragraphs
  if (node.type === 'paragraph') {
    const result: JSONContent = {
      type: 'paragraph',
      attrs: {},
      content: convertChildren(node.children)
    };

    // Extract ID from node data, node.id, or preceding comment
    let id = node.data?.hProperties?.['data-dg-id'] || node.data?.hProperties?.id || node.id;

    // If no ID found, check for preceding sibling comment
    if (!id && node.parent && Array.isArray(node.parent.children)) {
      const nodeIndex = node.parent.children.indexOf(node);
      if (nodeIndex > 0) {
        const prev = node.parent.children[nodeIndex - 1];
        let idMatch: RegExpMatchArray | null = null;
        if (prev && prev.type === 'html' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && prev.data?.estree?.comments) {
          // Handle MDX flow expression with comments in estree data
          const comments = prev.data.estree.comments;
          if (Array.isArray(comments) && comments.length > 0) {
            const comment = comments[0];
            if (comment.type === 'Block' && comment.value) {
              idMatch = comment.value.match(/^\s*id=([^\s]+)\s*$/);
            }
          }
        }
        if (idMatch) {
          id = idMatch[1].trim();
        }
      }
    }

    // Also check for inline ID comments within the paragraph
    if (!id && node.children && node.children.length > 0) {
      const firstChild = node.children[0];
      if (firstChild?.type === 'text' && firstChild.value?.includes('{/* id=')) {
        const match = firstChild.value.match(/\{\*\s*id=([^\s}]+)\s*\*\}/);
        if (match) {
          id = match[1];
          // Remove the comment from content
          const remainingText = firstChild.value.replace(/\{\*\s*id=[^}]+\*\}\s*/, '');
          if (remainingText) {
            result.content![0] = { type: 'text', text: remainingText };
          } else {
            result.content = result.content!.slice(1);
          }
        }
      }
    }

    if (id && id !== null && id !== '') {
      result.attrs!['id'] = String(id);
    }

    // Remove empty attrs object if no attributes were set
    if (Object.keys(result.attrs!).length === 0) {
      delete result.attrs;
    }

    return result;
  }

  // Handle headings
  if (node.type === 'heading') {
    const result: JSONContent = {
      type: 'heading',
      attrs: {},
      content: convertChildren(node.children)
    };

    // Extract ID from node data or preceding comment
    let id = node.data?.hProperties?.['data-dg-id'] || node.data?.hProperties?.id;

    // If no ID found, check for preceding sibling comment
    if (!id && node.parent && Array.isArray(node.parent.children)) {
      const nodeIndex = node.parent.children.indexOf(node);
      if (nodeIndex > 0) {
        const prev = node.parent.children[nodeIndex - 1];
        let idMatch: RegExpMatchArray | null = null;
        if (prev && prev.type === 'html' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && prev.data?.estree?.comments) {
          // Handle MDX flow expression with comments in estree data
          const comments = prev.data.estree.comments;
          if (Array.isArray(comments) && comments.length > 0) {
            const comment = comments[0];
            if (comment.type === 'Block' && comment.value) {
              idMatch = comment.value.match(/^\s*id=([^\s]+)\s*$/);
            }
          }
        }
        if (idMatch) {
          id = idMatch[1].trim();
        }
      }
    }

    // Also check for inline ID comments within the heading
    if (!id && node.children && node.children.length > 0) {
      const firstChild = node.children[0];
      if (firstChild?.type === 'text' && firstChild.value?.includes('{/* id=')) {
        const match = firstChild.value.match(/\{\/\*\s*id=([^\s}]+)\s*\*\/\}/);
        if (match) {
          id = match[1];
          // Remove the comment from content
          const remainingText = firstChild.value.replace(/\{\/\*\s*id=[^}]+\*\/\}\s*/, '');
          if (remainingText) {
            result.content![0] = { type: 'text', text: remainingText };
          } else {
            result.content = result.content!.slice(1);
          }
        }
      }
    }

    // Set attributes in the correct order: id first, then level
    if (id && id !== null && id !== '') {
      result.attrs!['id'] = String(id);
    }
    result.attrs!.level = node.depth;

    return result;
  }

  // Handle code blocks
  if (node.type === 'code') {
    // Handle Mermaid diagrams specially
    if (node.lang === 'mermaid') {
      return {
        type: 'mermaid',
        attrs: { code: node.value || '' }
      };
    }
    
    return {
      type: 'codeBlock',
      attrs: { language: node.lang || '' },
      content: [{ type: 'text', text: node.value || '' }]
    };
  }

  // Handle blockquotes
  if (node.type === 'blockquote') {
    return {
      type: 'blockquote',
      content: convertChildren(node.children)
    };
  }

  // Handle lists
  if (node.type === 'list') {
    const attrs: any = {};
    
    if (node.ordered) {
      if (node.start) {
        attrs.start = node.start;
      }
    } else {
      // For bullet lists, add tight attribute if present
      if (typeof node.tight === 'boolean') {
        attrs.tight = node.tight;
      }
    }
    
    return {
      type: node.ordered ? 'orderedList' : 'bulletList',
      attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
      content: convertChildren(node.children)
    };
  }

  // Handle list items
  if (node.type === 'listItem') {
    const result: JSONContent = {
      type: 'listItem',
      content: convertChildren(node.children)
    };

    // Extract ID from node data or preceding comment
    let id = node.data?.hProperties?.['data-dg-id'] || node.data?.hProperties?.id;

    // If no ID found, check for preceding sibling comment
    if (!id && node.parent && Array.isArray(node.parent.children)) {
      const nodeIndex = node.parent.children.indexOf(node);
      if (nodeIndex > 0) {
        const prev = node.parent.children[nodeIndex - 1];
        let idMatch: RegExpMatchArray | null = null;
        if (prev && prev.type === 'html' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && typeof prev.value === 'string') {
          idMatch = prev.value.match(/^\/\* id=([^*]+)\*\/$/); 
        } else if (prev && prev.type === 'mdxFlowExpression' && prev.data?.estree?.comments) {
          // Handle MDX flow expression with comments in estree data
          const comments = prev.data.estree.comments;
          if (Array.isArray(comments) && comments.length > 0) {
            const comment = comments[0];
            if (comment.type === 'Block' && comment.value) {
              idMatch = comment.value.match(/^\s*id=([^\s]+)\s*$/);
            }
          }
        }
        if (idMatch) {
          id = idMatch[1].trim();
        }
      }
    }

    // Also check for inline ID comments in the content
    if (!id && result.content && result.content.length > 0) {
      const firstChild = result.content[0];
      if (firstChild.type === 'paragraph' && firstChild.content && firstChild.content.length > 0) {
        const firstTextNode = firstChild.content[0];
        if (firstTextNode.type === 'text' && firstTextNode.text?.includes('{/* id=')) {
          const match = firstTextNode.text.match(/\{\s*\/\*\s*id=([^\s}]+)\s*\*\/\s*}\s*/);
          if (match) {
            id = match[1];
            // Remove the comment from the text content
            const remainingText = firstTextNode.text.replace(/\{\s*\/\*\s*id=[^}]+\*\/\s*}\s*/, '');
            if (remainingText.trim()) {
              firstTextNode.text = remainingText;
            } else {
              // Remove the empty text node
              firstChild.content = firstChild.content.slice(1);
              if (firstChild.content.length === 0) {
                result.content = result.content.slice(1);
              }
            }
          }
        }
      }
    }

    if (id && id !== null && id !== '') {
      result.attrs = { 'id': String(id) };
    }

    return result;
  }

  // Handle images
  if (node.type === 'image') {
    return {
      type: 'image',
      attrs: {
        src: node.url,
        alt: node.alt || '',
        title: node.title
      }
    };
  }

  // Handle hard breaks
  if (node.type === 'break') {
    return { type: 'hardBreak' };
  }

  // Handle horizontal rules
  if (node.type === 'thematicBreak') {
    return { type: 'horizontalRule' };
  }

  // Handle tables
  if (node.type === 'table') {
    return {
      type: 'table',
      content: convertChildren(node.children)
    };
  }

  // Handle table rows
  if (node.type === 'tableRow') {
    return {
      type: 'tableRow',
      content: convertChildren(node.children)
    };
  }

  // Handle table cells
  if (node.type === 'tableCell') {
    const cellChildren = convertChildren(node.children);
    return {
      type: 'tableCell',
      content: normalizeTableCellContent(cellChildren)
    };
  }

  // Handle table headers
  if (node.type === 'tableHeader') {
    const headerChildren = convertChildren(node.children);
    return {
      type: 'tableHeader',
      content: normalizeTableCellContent(headerChildren)
    };
  }

  // Handle MDX JSX elements (custom components)
  if (node.type === 'mdxJsxFlowElement' || node.type === 'mdxJsxTextElement') {
    const componentName = node.name;
    const attrs = extractAttributes(node);

    // Convert component names to TipTap node names for inline components
    const componentNameMap: Record<string, string> = {
      'InlineButton': 'inlineButton',
      'InlineImage': 'inlineImage',
      'Emoji': 'emoji',
      'Toggle': 'toggle',
    };
    
    const tiptapNodeName = componentNameMap[componentName] || componentName;

    const result: JSONContent = {
      type: tiptapNodeName
    };

    // Process attributes to match TipTap structure
    if (Object.keys(attrs).length > 0) {
      const processedAttrs: Record<string, any> = {};
      const nodeAttrs: Record<string, any> = {};

      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'id') {
          processedAttrs.id = value;
        } else if (key === 'data-dg-id') {
          // Special case for data-dg-id - map it to the original id attribute
          processedAttrs.id = value;
        } else if (key === 'stepId' && componentName === 'Screenshot') {
          // Special case for Screenshot: convert stepId back to step_id
          processedAttrs.step_id = value;
        } else if (key.startsWith('data-')) {
          // Convert data- attributes back to original names
          const originalKey = key.substring(5).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
          nodeAttrs[originalKey] = value;
        } else {
          processedAttrs[key] = value;
        }
      });

      // Combine all attributes without filtering
      const allAttrs = { ...processedAttrs, ...nodeAttrs };

      if (Object.keys(allAttrs).length > 0) {
        result.attrs = allAttrs;
      }
    }

    // Add content after attrs to match expected structure
    const children = convertChildren(node.children);
    
    // Some components like Screenshot, Citation, and inline components don't have content in the original structure
    // Only add content if there are actual children
    const componentsWithoutContent = ['Screenshot', 'Citation', 'inlineButton', 'inlineImage', 'emoji'];
    if (children.length > 0) {
      result.content = children;
    } else if (!componentsWithoutContent.includes(tiptapNodeName)) {
      // For components that normally have content, add empty array
      result.content = [];
    }

    return result;
  }

  // Handle subscript and superscript as inline marks (like parseMarkdownToTiptapDoc)
  if (node.type === 'subscript' || node.type === 'sub') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { type: 'sub' });
  }

  if (node.type === 'superscript' || node.type === 'sup') {
    const children = convertChildren(node.children);
    return addMarkToChildren(children, { type: 'sup' });
  }

  // Handle HTML elements (for underline, subscript, superscript)
  if (node.type === 'html') {
    const html = node.value;

    // Handle underline
    if (html === '<u>') {
      return { type: 'text', text: '', marks: [{ type: 'underline' }] };
    }
    if (html === '</u>') {
      return { type: 'text', text: '' };
    }

    // Handle subscript
    if (html === '<sub>') {
      return { type: 'text', text: '', marks: [{ type: 'sub' }] };
    }
    if (html === '</sub>') {
      return { type: 'text', text: '' };
    }

    // Handle superscript
    if (html === '<sup>') {
      return { type: 'text', text: '', marks: [{ type: 'sup' }] };
    }
    if (html === '</sup>') {
      return { type: 'text', text: '' };
    }

    return { type: 'text', text: '' };
  }

  // Handle root document
  if (node.type === 'root') {
    return {
      type: 'doc',
      content: convertChildren(node.children)
    };
  }

  // Default: convert children if they exist
  if (node.children) {
    const children = convertChildren(node.children);
    if (children.length === 1) {
      return children[0];
    }
    return children;
  }

  // Fallback for unknown nodes
  return { type: 'text', text: '' };
}

/**
 * Convert array of MDAST children to TipTap content
 */
function convertChildren(children: any[]): JSONContent[] {
  if (!children || !Array.isArray(children)) {
    return [];
  }

  const result: JSONContent[] = [];

  for (const child of children) {
    const converted = mdastToTiptap(child);

    if (Array.isArray(converted)) {
      result.push(...converted);
    } else if (typeof converted === 'string') {
      if (converted) {
        result.push({ type: 'text', text: converted });
      }
    } else if (converted && typeof converted === 'object' && 'type' in converted) {
      result.push(converted as JSONContent);
    }
  }

  return mergeTextNodes(result);
}

/**
 * Merge adjacent text nodes with same marks
 */
function mergeTextNodes(nodes: JSONContent[]): JSONContent[] {
  const merged: JSONContent[] = [];

  for (const node of nodes) {
    if (node.type === 'text' && merged.length > 0) {
      const lastNode = merged[merged.length - 1];
      if (lastNode.type === 'text' &&
        JSON.stringify(lastNode.marks || []) === JSON.stringify(node.marks || [])) {
        // Merge with previous text node
        lastNode.text = (lastNode.text || '') + (node.text || '');
        continue;
      }
    }
    merged.push(node);
  }

  return merged;
}

const INLINE_TABLE_INLINE_TYPES = new Set([
  'emoji',
  'inlineButton',
  'inlineImage',
  'mention',
  'Glossary',
  'Citation',
  'sourceCitation',
]);

function isInlineTableContent(node: JSONContent): boolean {
  if (!node || typeof node !== 'object') {
    return false;
  }

  if (node.type === 'text' || node.type === 'hardBreak') {
    return true;
  }

  return node.type ? INLINE_TABLE_INLINE_TYPES.has(node.type) : false;
}

function normalizeTableCellContent(children: JSONContent[]): JSONContent[] {
  if (!children || children.length === 0) {
    return [{ type: 'paragraph', content: [] }];
  }

  const normalized: JSONContent[] = [];
  let inlineBuffer: JSONContent[] = [];

  const flushInlineBuffer = () => {
    if (inlineBuffer.length > 0) {
      normalized.push({
        type: 'paragraph',
        content: [...inlineBuffer],
      });
      inlineBuffer = [];
    }
  };

  for (const child of children) {
    if (isInlineTableContent(child)) {
      inlineBuffer.push(child);
      continue;
    }

    flushInlineBuffer();
    normalized.push(child);
  }

  flushInlineBuffer();

  if (normalized.length === 0) {
    normalized.push({ type: 'paragraph', content: [] });
  }

  return normalized;
}

/**
 * Add marks to text nodes in children
 */
function addMarkToChildren(children: JSONContent[], mark: any): JSONContent[] {
  return children.map(child => {
    if (child.type === 'text') {
      return {
        ...child,
        marks: child.marks ? [...child.marks, mark] : [mark]
      };
    }
    return child;
  });
}

/**
 * Process MDX comments to extract IDs
 */
function processIdComments(tree: any) {
  visit(tree, 'text', (node: any, index: number | undefined, parent: any) => {
    if (node.value && node.value.includes('{/* id=')) {
      const match = node.value.match(/\{\/\*\s*id=([^\s}]+)\s*\*\/\}/);
      if (match && parent && typeof index === 'number') {
        // Store the ID on the parent node
        parent.id = match[1];

        // Remove the comment from the text
        node.value = node.value.replace(/\{\/\*\s*id=[^}]+\*\/\}\s*/, '');

        // If the text is now empty, remove the node
        if (!node.value.trim() && parent.children) {
          parent.children.splice(index, 1);
        }
      }
    }
  });
  
  // Also process MDX flow expressions that might contain ID comments
  visit(tree, 'mdxFlowExpression', (node: any, index: number | undefined, parent: any) => {
    if (node.value && node.value.includes('/* id=')) {
      const match = node.value.match(/^\s*\/\*\s*id=([^\s}]+)\s*\*\/\s*$/);
      if (match && parent && typeof index === 'number') {
        // Find the next sibling that should get this ID
        const nextSibling = parent.children[index + 1];
        if (nextSibling) {
          nextSibling.id = match[1];
          // Remove the comment node
          parent.children.splice(index, 1);
        }
      }
    }
  });
}

/**
 * Result of deserializing MDX with frontmatter
 */
export interface DeserializeResult {
  content: JSONContent;
  frontmatter: Record<string, any>;
}

/**
 * Deserialize MDX string to TipTap JSON format with frontmatter
 */
// Regex to match {{PLACEHOLDER}} patterns for escaping
const TEMPLATE_PLACEHOLDER_REGEX = /\{\{([A-Z_]+)\}\}/g;

function escapeTemplatePlaceholders(content: string): string {
  return content.replace(TEMPLATE_PLACEHOLDER_REGEX, `${PLACEHOLDER_ESCAPE_PREFIX}$1${PLACEHOLDER_ESCAPE_SUFFIX}`);
}

export function deserializeFromMarkdown(mdx: string): DeserializeResult {
  try {
    // Parse frontmatter using gray-matter
    const { content: mdxContent, data: frontmatter } = matter(mdx);

    // Escape {{PLACEHOLDER}} patterns before MDX parsing
    const escapedContent = escapeTemplatePlaceholders(mdxContent);

    // Parse MDX to MDAST
    const processor = unified()
      .use(remarkParse)
      .use(remarkMdx)
      .use(remarkGfm)
      .use(remarkDirective)
      .use(remarkHighlight)
      .use(remarkCustomAttributes);

    const tree = processor.parse(escapedContent);
    const processedTree = processor.runSync(tree);

    // Process ID comments
    processIdComments(processedTree);

    // Convert MDAST to TipTap JSON
    const result = mdastToTiptap(processedTree);

    // Ensure we return a document node
    let content: JSONContent;
    if (Array.isArray(result)) {
      content = {
        type: 'doc',
        content: result
      };
    } else if (result.type === 'doc') {
      content = result;
    } else {
      content = {
        type: 'doc',
        content: [result]
      };
    }
    
    return {
      content,
      frontmatter
    };
  } catch (error) {
    console.error('Error deserializing MDX:', error);
    return {
      content: {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: 'Error parsing content'
              }
            ]
          }
        ]
      },
      frontmatter: {}
    };
  }
}

/**
 * Legacy function that returns only content (for backward compatibility)
 */
export function deserializeFromMarkdownLegacy(mdx: string): JSONContent {
  const result = deserializeFromMarkdown(mdx);
  return result.content;
}

/**
 * Parse Markdown to TipTap document (consolidated from tiptap-mdx-mdx-bridge.ts)
 * This function provides the same functionality as parseMarkdownToTiptapDoc
 * but reuses the enhanced logic from deserializeFromMarkdown
 */
export function parseMarkdownToTiptapDoc(markdown: string): JSONContent {
  const result = deserializeFromMarkdown(markdown);
  return result.content;
}

/**
 * Parse Markdown to TipTap document with frontmatter
 */
export function parseMarkdownToTiptapDocWithFrontmatter(markdown: string): DeserializeResult {
  return deserializeFromMarkdown(markdown);
}

/**
 * Test bidirectional conversion
 */
export function testBidirectionalConversion(originalJson: JSONContent, renderToMarkdown: (content: JSONContent) => string): boolean {
  try {
    // Convert JSON to MDX
    const mdx = renderToMarkdown(originalJson);

    // Convert MDX back to JSON
    const result = deserializeFromMarkdown(mdx);
    const reconstructedJson = result.content;

    // Compare the structures (simplified comparison)
    return JSON.stringify(originalJson) === JSON.stringify(reconstructedJson);
  } catch (error) {
    console.error('Bidirectional conversion test failed:', error);
    return false;
  }
}
